import { useState } from "react";
import { createStore, applyMiddleware, Store } from "redux";
import { composeWithDevTools } from "redux-devtools-extension";
import { RootState, RootAction } from "@/lib/redux/types";
import { rootReducer, getPaymentFrequencyDefaults } from "@/lib/redux/reducers";
import {
  FormConfig,
  FreeFormAddressElementType,
} from "@evergiving/common/dist/es2015/form";
import {
  PaymentSourceKind,
  Versioned,
  PostalAddressKind,
} from "@evergiving/common/dist/es2015/pledge";
import thunkMiddleware from "redux-thunk";

// This file adapted from https://github.com/vercel/next.js/blob/v10.0.3/examples/with-redux/store.js

type FormStore = Store<RootState, RootAction>;

let store: FormStore | undefined;

const initialVersion: Versioned<string | undefined> = {
  value: undefined,
  version: 0,
};

export function initialStateFromFormConfig(formConfig: FormConfig): RootState {
  const defaultPaymentFrequency =
    "payment" in formConfig ? formConfig.payment.defaultFrequency : undefined;

  const defaultPaymentSourceKind =
    "payment" in formConfig
      ? [PaymentSourceKind.CreditCard, PaymentSourceKind.DirectDebit]
          .map((paymentSourceKind) => ({
            paymentSourceKind,
            index: Object.keys(formConfig.payment).indexOf(paymentSourceKind),
          }))
          .filter(({ index }) => index >= 0)
          .sort((a, b) => a.index - b.index)[0]
      : undefined;

  return {
    firstName: initialVersion,
    lastName: initialVersion,
    phoneNumber: initialVersion,
    emailAddress: initialVersion,
    cardHolder: initialVersion,
    cardNumber: initialVersion,
    cardExpiry: initialVersion,
    cardVerificationCode: initialVersion,
    directDebitAccountHolder: initialVersion,
    directDebitAccountNumber: initialVersion,
    directDebitSortCode: initialVersion,
    directDebitIBAN: initialVersion,
    dateOfBirth: initialVersion,
    gender: { value: undefined, version: 0 },
    belfioreCode: initialVersion,
    profession: initialVersion,
    title: initialVersion,

    [FreeFormAddressElementType.Element3]: initialVersion,
    [FreeFormAddressElementType.Element4]: initialVersion,
    [FreeFormAddressElementType.Element5]: initialVersion,
    [FreeFormAddressElementType.Element6]: initialVersion,
    [FreeFormAddressElementType.Element7]: initialVersion,
    [FreeFormAddressElementType.Element8]: initialVersion,
    [FreeFormAddressElementType.Element9]: initialVersion,
    [FreeFormAddressElementType.Element10]: initialVersion,
    [FreeFormAddressElementType.Element11]: initialVersion,
    [FreeFormAddressElementType.Element12]: initialVersion,
    [FreeFormAddressElementType.Element13]: initialVersion,
    [FreeFormAddressElementType.Element14]: initialVersion,
    [FreeFormAddressElementType.Element15]: initialVersion,

    giftAidConsent: false,
    termsAndConditionsConsent: false,
    phoneContactConsent: false,
    smsContactConsent: false,
    emailContactConsent: false,
    postContactConsent: false,

    emailAppraisal: undefined,
    ibanAppraisal: undefined,
    testingReference: undefined,
    previousSubmissionState: undefined,
    formSourceRef: undefined,

    errors: {},
    countryCode:
      "country" in formConfig
        ? initialVersion
        : { value: formConfig.defaultCountryCode, version: 1 },
    paymentFrequency: defaultPaymentFrequency,
    ...getPaymentFrequencyDefaults(
      formConfig,
      defaultPaymentFrequency,
      defaultPaymentSourceKind?.paymentSourceKind
    ),
    paymentSourceKind: defaultPaymentSourceKind?.paymentSourceKind,
    paymentDay: undefined,

    ...(formConfig.address && !("melissa" in formConfig.address)
      ? {
          postalAddress: {
            kind: PostalAddressKind.FreeForm,
            element2: formConfig.defaultCountryCode,
            element3: undefined,
            element4: undefined,
            element5: undefined,
            element6: undefined,
            element7: undefined,
            element8: undefined,
            element9: undefined,
            element10: undefined,
            element11: undefined,
            element12: undefined,
            element13: undefined,
            element14: undefined,
            element15: undefined,
          },
        }
      : {}),
  };
}

function initStore(preloadedState: RootState): FormStore {
  return createStore(
    rootReducer,
    preloadedState,
    composeWithDevTools(applyMiddleware(thunkMiddleware))
  );
}

export const initializeStore = (preloadedState: RootState): FormStore => {
  let newStore = store ?? initStore(preloadedState);

  // After navigating to a page with an initial Redux state, merge that state
  // with the current state in the store, and create a new store
  if (preloadedState && store) {
    newStore = initStore({
      ...store.getState(),
      ...preloadedState,
    });
    // Reset the current store
    store = undefined;
  }

  // For SSG and SSR always create a new store
  if (typeof window === "undefined") return newStore;
  // Create the store once in the client
  if (!store) store = newStore;

  return newStore;
};

export function useStore(formConfig: FormConfig): FormStore {
  const [persistentStore] = useState<FormStore>(
    initializeStore(initialStateFromFormConfig(formConfig))
  );
  return persistentStore;
}
